import { entries, isArray, toJSON } from '@rehaje/shared'

import { YArray, YMap } from './types'

import type {
  DocChangedListener,
  ReactiveChangedListener,
  YStruct,
  YValue
} from './types'

export const defaultOnDocChanged: DocChangedListener = (events, next) => {
  next(events)
}

export const defaultOnReactiveChanged: ReactiveChangedListener = (
  event,
  index,
  events,
  next
) => {
  next(event)
}

export function deleteYjs(yStruct: YStruct, key: string) {
  if (yStruct instanceof YArray && !isNaN(key)) {
    yStruct.delete(Number(key))
  } else if (yStruct instanceof YMap && typeof key === 'string') {
    yStruct.delete(key)
  }
}

export function ensurePath(
  yStruct: undefined | YStruct,
  target: any,
  path: PropertyKey[]
): undefined | YStruct {
  for (const key of path) {
    target = target?.[key]
    if (!yStruct) return
    if (yStruct instanceof YArray && !isNaN(key)) {
      const index = Number(key)
      if (!yStruct.get(index)) {
        const newShared = createYStruct(target)
        if (newShared) {
          if (index >= 0 && index < yStruct.length) yStruct.delete(index)
          yStruct.insert(index, [newShared])
        }
      }
      yStruct = yStruct.get(index)
    } else if (yStruct instanceof YMap) {
      if (typeof key !== 'string') return
      if (!yStruct.get(key)) {
        const newShared = createYStruct(target)
        if (newShared) yStruct.set(key, newShared)
      }
      yStruct = yStruct.get(key)
    }
  }
  return yStruct
}

export function initYjsRootMap(yRoot: YMap, target?: any) {
  if (typeof target === 'object' && target !== null) {
    for (const [key, value] of entries(toJSON(target))) {
      yRoot.set(key, json2yjs(value))
    }
  }
}

export function json2yjs(target?: any): YValue {
  return internalJson2yjs(toJSON(target))
}

export function setYjs(yStruct: YStruct, key: string, value: any) {
  if (yStruct instanceof YArray && !isNaN(key)) {
    const index = Number(key)
    if (index >= 0 && index < yStruct.length) yStruct.delete(index)
    yStruct.insert(index, [json2yjs(value)])
  } else if (yStruct instanceof YMap && typeof key === 'string') {
    yStruct.set(key, json2yjs(value))
  }
}

export function spliceYjs(
  yArray: YArray,
  start: number,
  deleteCount: number,
  items: any[]
) {
  if (yArray instanceof YArray) {
    if (deleteCount > 0 && start >= 0 && start < yArray.length) {
      yArray.delete(start, deleteCount)
    }
    if (items.length > 0) {
      const yItems = items.map((item) => json2yjs(item))
      yArray.insert(start, yItems)
    }
  }
}

export function yjs2json(target?: YValue): any {
  return toJSON(internalYjs2json(target))
}

function createYStruct(target?: any): undefined | YStruct {
  if (isArray(target)) return new YArray()
  if (typeof target === 'object' && target !== null) return new YMap()
}

function internalJson2yjs(target?: any): YValue {
  if (isArray(target)) {
    const yArray = new YArray()
    for (const item of target) {
      yArray.push([internalJson2yjs(item)])
    }
    return yArray
  } else if (typeof target === 'object' && target !== null) {
    const yMap = new YMap()
    for (const [key, value] of entries(target)) {
      yMap.set(key, internalJson2yjs(value))
    }
    return yMap
  } else return target
}

function internalYjs2json(target?: YValue): any {
  if (target instanceof YArray) return target.toJSON()
  if (target instanceof YMap) return target.toJSON()
  return target
}
